import { apiFetch } from "@/lib/api";
import type { MeDto } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface SessionContextValue {
  me: MeDto | null;
  loading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  signout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}

export function SessionProvider({
  initialMe = null,
  children,
}: {
  initialMe?: MeDto | null;
  children: ReactNode;
}) {
  const [me, setMe] = useState<MeDto | null>(initialMe);
  const [loading, setLoading] = useState(initialMe === null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<MeDto>("/api/auth/me", {
        suppressAuthRedirect: true,
      });
      setMe(data);
    } catch {
      setMe({ authenticated: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialMe === null) {
      void refresh();
    }
  }, [initialMe, refresh]);

  const signout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/signout", { method: "POST", suppressAuthRedirect: true });
    } catch {
      // Ignore; clearing local state is what matters.
    }
    setMe({ authenticated: false });
    navigate("/");
  }, [navigate]);

  const isAdmin = me?.authenticated === true && me.role === "Administrator";

  // The session wraps the entire app, so a fresh object here would re-render every
  // consumer on each render of any ancestor.
  const value = useMemo(
    () => ({ me, loading, isAdmin, refresh, signout }),
    [me, loading, isAdmin, refresh, signout],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}
